import Box from '@app/components/common/Box';
import BreadCrumbSettings from '@app/components/LoggedIn/Settings/BreadCrumbSettings';
import { getDatesByReportPeriod } from '@app/helpers/management/employees/employees';
import { selectOrganizationId } from '@app/state/selectors/appSelectors';
import {
  fetchEmployeeDetailsShiftsWeb,
  fetchEmployeeDetailsSummaryWeb,
  fetchEmployeesWeb,
} from '@app/state/settings/management/employeesWebActions';
import { useAppDispatch } from '@app/state/store';
import { selectLoggedInRoleId } from '@app/state/users/usersSelectors';
import { displayFlexColumnStyles } from '@app/theme/commonStyles';
import { bindActionCreators } from '@reduxjs/toolkit';
import {
  actionCreatorsApp,
  EMPLOYEE_DETAILS_SCREEN_FILTERS_INITIAL_STATE,
  EMPLOYEES_SCREEN_FILTERS_INITIAL_STATE,
  EReportPeriod,
  IEmployeeDetailsScreenFilters,
  IEmployeeShiftSorting,
  IEmployeesScreenFilters,
  IEmployeesScreenSorting,
  TEditEmployeeShift,
  TEmployeeDetailsScreenAmountsResponse,
  TEmployeeDetailsScreenShift,
  TEmployeesMainScreenRow,
  usePrevious,
} from '@westondev/tableturn-core';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import EmployeeDetails from './EmployeeDetails';
import { EmployeesScreenContext } from './EmployeesContext';
import EmployeesTable from './EmployeesTable';
import TopBar from './TopBar';

const Employees = () => {
  const dispatch = useAppDispatch();

  const organizationId = useSelector(selectOrganizationId);
  const loggedInRoleId = useSelector(selectLoggedInRoleId);

  const { setIsLoadingModal } = bindActionCreators(actionCreatorsApp, dispatch);

  const [user, setUser] = useState<TEmployeesMainScreenRow | null>(null);
  const [shift, setShift] = useState<TEditEmployeeShift | null>(null);
  const [filters, _setFilters] = useState<IEmployeesScreenFilters>({
    ...EMPLOYEES_SCREEN_FILTERS_INITIAL_STATE,
    ...getDatesByReportPeriod(EReportPeriod.TODAY),
  });
  const [users, setUsers] = useState<TEmployeesMainScreenRow[]>([]);

  const [isFetching, setIsFetching] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [page, setPage] = useState(-1);
  const [totalPages, setTotalPages] = useState(0);
  const [count, setCount] = useState(0);
  const [sorting, setSorting] = useState<IEmployeesScreenSorting>({
    field: 'name',
    direction: 'asc',
  });

  const [shiftsSorting, setShiftsSorting] = useState<IEmployeeShiftSorting>({
    field: 'shiftStart',
    direction: 'asc',
  });

  const [detailsFilters, _setDetailsFilters] =
    useState<IEmployeeDetailsScreenFilters>({
      ...EMPLOYEE_DETAILS_SCREEN_FILTERS_INITIAL_STATE,
      ...getDatesByReportPeriod(EReportPeriod.TODAY),
    });

  const [isDetailsFetching, setIsDetailsFetching] = useState(false);
  const [hasDetailsFetched, setHasDetailsFetched] = useState(false);
  const [detailsPage, setDetailsPage] = useState(-1);
  const [detailsTotalPages, setDetailsTotalPages] = useState(0);
  const [detailsCount, setDetailsCount] = useState(0);

  const [shifts, setShifts] = useState<TEmployeeDetailsScreenShift[]>([]);

  const [employeeSummary, setEmployeeSummary] =
    useState<TEmployeeDetailsScreenAmountsResponse | null>(null);

  const previousFilters = usePrevious(filters);
  const previousSorting = usePrevious(sorting);
  const previousShiftsSorting = usePrevious(shiftsSorting);
  const previousDetailsFilters = usePrevious(detailsFilters);

  const fetchEmployees = useCallback(
    async (append: boolean, overrideSorting?: IEmployeesScreenSorting) => {
      if (!organizationId || !loggedInRoleId) {
        return;
      }
      if (append && (totalPages <= page + 1 || !hasFetched || isFetching))
        return;
      setIsFetching(true);
      const {
        success,
        totalPages: newTotalPages,
        total,
        currentPage,
        employees,
      } = await dispatch(
        fetchEmployeesWeb(
          filters,
          overrideSorting || sorting,
          append ? page + 1 : 0,
          users,
          append,
        ),
      );
      setIsFetching(false);
      if (!success) return;
      setHasFetched(true);
      setCount(total);
      setTotalPages(newTotalPages);
      setPage(currentPage);
      setUsers(employees);
    },
    [
      totalPages,
      page,
      hasFetched,
      isFetching,
      dispatch,
      filters,
      users,
      sorting,
      organizationId,
      loggedInRoleId,
    ],
  );

  const fetchShifts = useCallback(
    async (append: boolean, overrideOmit?: boolean) => {
      if (
        append &&
        (detailsTotalPages <= detailsPage + 1 ||
          !hasDetailsFetched ||
          isDetailsFetching)
      )
        return;
      setIsDetailsFetching(true);
      const {
        success,
        totalPages: newTotalPages,
        total,
        currentPage,
        shifts: newShifts,
      } = await dispatch(
        fetchEmployeeDetailsShiftsWeb(
          detailsFilters,
          shiftsSorting,
          append ? detailsPage + 1 : 0,
          user?.orgUserId || 0,
          shifts,
          append,
          overrideOmit !== undefined ? overrideOmit : !append,
        ),
      );
      setIsDetailsFetching(false);
      if (!success) return;
      setHasDetailsFetched(true);
      setDetailsCount(total);
      setDetailsTotalPages(newTotalPages);
      setDetailsPage(currentPage);
      setShifts(newShifts);
    },
    [
      detailsFilters,
      detailsPage,
      detailsTotalPages,
      dispatch,
      hasDetailsFetched,
      isDetailsFetching,
      shifts,
      user?.orgUserId,
      shiftsSorting,
    ],
  );

  const fetchEmployeeSummary = useCallback(async () => {
    const { success, ...newEmployeeSummary } = await dispatch(
      fetchEmployeeDetailsSummaryWeb(detailsFilters, user?.orgUserId || 0),
    );
    if (!success) return;
    setEmployeeSummary(newEmployeeSummary);
  }, [detailsFilters, dispatch, user?.orgUserId]);

  const fetchAllDetails = useCallback(async () => {
    setIsLoadingModal({
      active: true,
      isMakingApiCall: true,
    });

    await fetchEmployeeSummary();
    await fetchShifts(false);
    setIsLoadingModal({ active: false, isMakingApiCall: false });
  }, [fetchEmployeeSummary, fetchShifts, setIsLoadingModal]);

  const setFilters = useCallback(
    (
      newPartialFilters: React.SetStateAction<Partial<IEmployeesScreenFilters>>,
    ) =>
      _setFilters(prevFilters => ({
        ...prevFilters,
        ...newPartialFilters,
      })),
    [_setFilters],
  );

  const setDetailsFilters = useCallback(
    (
      newPartialDetailsFilters: React.SetStateAction<
        Partial<IEmployeeDetailsScreenFilters>
      >,
    ) =>
      _setDetailsFilters(prevFilters => ({
        ...prevFilters,
        ...newPartialDetailsFilters,
      })),
    [_setDetailsFilters],
  );

  const contextValue = useMemo(
    () => ({
      user,
      setUser,
      shift,
      setShift,
      filters,
      setFilters,
      users,
      setUsers,
      isFetching,
      setIsFetching,
      hasFetched,
      setHasFetched,
      page,
      setPage,
      totalPages,
      setTotalPages,
      count,
      setCount,
      fetchEmployees,
      isDetailsFetching,
      setIsDetailsFetching,
      hasDetailsFetched,
      setHasDetailsFetched,
      detailsPage,
      setDetailsPage,
      detailsTotalPages,
      setDetailsTotalPages,
      detailsCount,
      setDetailsCount,
      detailsFilters,
      setDetailsFilters,
      fetchShifts,
      shifts,
      setShifts,
      employeeSummary,
      setEmployeeSummary,
      fetchAllDetails,
      sorting,
      setSorting,
      shiftsSorting,
      setShiftsSorting,
    }),
    [
      user,
      shift,
      filters,
      setFilters,
      users,
      isFetching,
      hasFetched,
      page,
      totalPages,
      count,
      fetchEmployees,
      isDetailsFetching,
      hasDetailsFetched,
      detailsPage,
      detailsTotalPages,
      detailsCount,
      detailsFilters,
      setDetailsFilters,
      fetchShifts,
      shifts,
      employeeSummary,
      fetchAllDetails,
      shiftsSorting,
      sorting,
    ],
  );

  useEffect(() => {
    if (organizationId && loggedInRoleId) {
      setFilters(getDatesByReportPeriod(EReportPeriod.TODAY));
    }
  }, [organizationId, loggedInRoleId, setFilters]);

  useEffect(() => {
    if (
      filters.endDate &&
      filters.startDate &&
      (!isEqual(filters, previousFilters) || (!hasFetched && !isFetching))
    ) {
      fetchEmployees(false);
    }
  }, [
    filters,
    fetchEmployees,
    previousFilters,
    organizationId,
    hasFetched,
    loggedInRoleId,
    isFetching,
  ]);

  useEffect(() => {
    if (
      filters.endDate &&
      filters.startDate &&
      !isEqual(sorting, previousSorting)
    ) {
      fetchEmployees(false);
    }
  }, [filters, fetchEmployees, sorting, previousSorting]);

  useEffect(() => {
    if (
      user &&
      detailsFilters.endDate &&
      detailsFilters.startDate &&
      !isEqual(shiftsSorting, previousShiftsSorting)
    ) {
      fetchShifts(false, false);
    }
  }, [
    detailsFilters.endDate,
    detailsFilters.startDate,
    fetchShifts,
    previousShiftsSorting,
    shiftsSorting,
    user,
  ]);

  useEffect(() => {
    if (
      user &&
      detailsFilters.endDate &&
      detailsFilters.startDate &&
      !isEqual(detailsFilters, previousDetailsFilters)
    ) {
      fetchAllDetails();
    }
  }, [user, detailsFilters, fetchAllDetails, previousDetailsFilters]);

  return (
    <EmployeesScreenContext.Provider value={contextValue}>
      <Box
        csx={[
          displayFlexColumnStyles,
          {
            height: '100%',
            gap: 10,
          },
        ]}>
        {user && (
          <BreadCrumbSettings
            csx={{ padding: 0 }}
            title="Employees"
            currentPage={user?.name}
            onClick={() => {
              setUser(null);
              setEmployeeSummary(null);
              setShifts([]);
              setDetailsFilters(EMPLOYEE_DETAILS_SCREEN_FILTERS_INITIAL_STATE);
            }}
            isSettings={false}
          />
        )}

        <Box
          csx={theme => [
            displayFlexColumnStyles,
            {
              flexGrow: 1,
              flexShrink: 1,
              backgroundColor: user
                ? theme.colors.background
                : theme.colors.white,
              borderRadius: user ? undefined : 10,
              gap: 10,
              boxShadow: user ? undefined : theme.shadows[1],
            },
          ]}>
          <Box
            csx={theme => [
              displayFlexColumnStyles,
              {
                flexGrow: 0,
                padding: 10,
                boxShadow: user ? theme.shadows[2] : undefined,
                backgroundColor: user ? theme.colors.white : undefined,
                borderRadius: user ? 10 : undefined,
              },
            ]}>
            <TopBar />
          </Box>

          <Box
            csx={[
              displayFlexColumnStyles,
              {
                flexGrow: 1,
                flexShrink: 1,
                height: 0,
              },
            ]}>
            {user ? <EmployeeDetails /> : <EmployeesTable />}
          </Box>
        </Box>
      </Box>
    </EmployeesScreenContext.Provider>
  );
};

export default Employees;
